import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Location } from 'react-router';
import { bannerTargetsCurrentLocale } from '../../../common/bannerLocales';
import { getBannerRouteTargets, TBannerRouteTarget } from '../../../common/bannerRouteTargets';
import { stripPathLocale } from '../../../common/localeShared';
import { matchRoutePath } from '../../libs/router/router-helpers';
import { sanitizeRichHtml } from '../../libs/sanitizeContent';
import { chinaAudienceSelector } from '../../scripts/selectors/global';

export type TBanner = {
  id?: number;
  message: string;
  type: 'warn' | 'info' | 'notice';
  dismissable?: boolean;

  route?: string;
  routeKey?: string;
  routeValue?: string;
  routeTargets?: TBannerRouteTarget[];
  audience?: 'china';
  expiresAt?: string | null;
  locales?: string[];
};

type TBannerProps = {
  banners: TBanner[];
  currentLocale: string;
  location: Location;
  namedRoutes: Record<string, string>;
};

function shouldDisplayBanner(
  banner: TBanner,
  currentLocale: string,
  location: Location,
  namedRoutes: Record<string, string>
) {
  if (!banner.message) {
    return false;
  }

  if (banner.expiresAt && new Date(banner.expiresAt) < new Date()) {
    return false;
  }

  if (!bannerTargetsCurrentLocale(banner.locales, currentLocale)) {
    return false;
  }

  const currentPath = location?.pathname ? stripPathLocale(location.pathname) : '';
  let routeTargets: TBannerRouteTarget[];
  try {
    routeTargets = getBannerRouteTargets(banner, namedRoutes);
  } catch {
    return false;
  }

  if (routeTargets.length > 0 && !currentPath) {
    return false;
  }

  if (
    routeTargets.length > 0 &&
    !routeTargets.some((target) => {
      const match = matchRoutePath({ path: namedRoutes[target.route], end: false }, currentPath);
      if (!match) {
        return false;
      }

      return Object.entries(target.params || {}).every(([key, value]) => match.params[key] === value);
    })
  ) {
    return false;
  }

  return true;
}

function getDismissKey(id: number) {
  return `rio:banner-${id}-dismissed`;
}

export default function SiteBanner(props: TBannerProps) {
  const { banners, currentLocale, location, namedRoutes } = props;
  const isChinaAudience = useSelector((state) => chinaAudienceSelector(state, currentLocale));
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!banners || banners.length === 0) {
      return;
    }

    const dismissed = new Set<number>();
    for (const banner of banners) {
      if (banner.id != null) {
        try {
          if (localStorage.getItem(getDismissKey(banner.id)) === '1') {
            dismissed.add(banner.id);
          }
        } catch {
          // localStorage may be unavailable
        }
      }
    }

    if (dismissed.size > 0) {
      setDismissedIds(dismissed);
    }
  }, [banners]);

  if (!banners || banners.length === 0) {
    return null;
  }

  function handleDismiss(id: number) {
    try {
      localStorage.setItem(getDismissKey(id), '1');
    } catch {
      // localStorage may be unavailable
    }
    setDismissedIds((prev) => new Set(prev).add(id));
  }

  const bannerElements = [];
  for (const banner of banners) {
    const isDismissable = banner.id != null && banner.dismissable !== false;

    if (isDismissable && dismissedIds.has(banner.id!)) {
      continue;
    }

    if (banner.audience != null) {
      if (banner.audience !== 'china' || !isChinaAudience) {
        continue;
      }
    }

    if (!shouldDisplayBanner(banner, currentLocale, location, namedRoutes)) {
      continue;
    }

    bannerElements.push(
      <div key={banner.id ?? banner.message} className={cx('rio-site-banner', `rio-site-banner--${banner.type}`)}>
        <div
          className="rio-site-banner__content"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(banner.message) }}
        />
        {isDismissable && (
          <button className="rio-site-banner__close" onClick={() => handleDismiss(banner.id!)} aria-label="Dismiss">
            <i className="fa fa-times" />
          </button>
        )}
      </div>
    );
  }

  if (bannerElements.length > 0) {
    return <>{bannerElements}</>;
  }

  return null;
}
